import type {
  TranslateFormDraftContentInput,
  TranslateFormDraftContentPayload,
} from 'src/graphql';
import type { LanguageOption } from 'src/utils/languages';
import type { QuestionData } from '../types';
import { buildFormDraftTranslateContentInput } from './buildFormDraftTranslateContentInput';
import { DEFAULT_FORM_EDITING_LOCALE } from './localizedQuestionFieldHelpers';
import { mergeFormTranslateDraftContentIntoQuestions } from './mergeFormTranslateDraftContentIntoQuestions';

function localeHasNonEmptyTranslations(
  questions: QuestionData[],
  languageCode: string,
): boolean {
  // Allow translation even with 0 questions (for COI form title/description)
  if (questions.length === 0) return true;
  return questions.every((q) => {
    const row = q.translations?.find((t) => t.languageCode === languageCode);
    return row != null && row.questionText.trim().length > 0;
  });
}

export async function ensureFormDraftTranslationsBeforeSave(params: {
  questions: QuestionData[];
  formTitle: string;
  formDescription: string;
  selectedLanguages: LanguageOption[];
  translate: (
    input: TranslateFormDraftContentInput,
  ) => Promise<TranslateFormDraftContentPayload>;
}): Promise<{
  questions: QuestionData[];
  warnings: string[];
  titleTranslations: Record<string, string>;
  descriptionTranslations: Record<string, string>;
}> {
  const warnings: string[] = [];
  const titleTranslations: Record<string, string> = {};
  const descriptionTranslations: Record<string, string> = {};
  const targets = params.selectedLanguages.filter(
    (l) => l.value !== DEFAULT_FORM_EDITING_LOCALE,
  );

  const langsNeedingTranslation = targets.filter(
    (lang) => !localeHasNonEmptyTranslations(params.questions, lang.value),
  );

  if (langsNeedingTranslation.length === 0) {
    return {
      questions: params.questions,
      warnings,
      titleTranslations,
      descriptionTranslations,
    };
  }

  const translationResults = await Promise.all(
    langsNeedingTranslation.map(async (lang) => {
      const input = buildFormDraftTranslateContentInput({
        targetLanguageCode: lang.value,
        formTitle: params.formTitle,
        formDescription: params.formDescription,
        questions: params.questions,
      });
      const payload = await params.translate(input);
      return { locale: lang.value, payload };
    }),
  );

  let result = params.questions;
  for (const { locale, payload } of translationResults) {
    warnings.push(...(payload.warnings ?? []));

    // Capture title/description translations
    if (payload.formTitle) {
      titleTranslations[locale] = payload.formTitle;
    }
    if (payload.formDescription) {
      descriptionTranslations[locale] = payload.formDescription;
    }

    const patchedPayload = {
      ...payload,
      questions: (payload.questions ?? []).map((tq) => {
        const source = params.questions[tq.orderIndex];
        return {
          ...tq,
          questionText: tq.questionText?.trim() || source?.questionText || '',
          description: tq.description ?? source?.description ?? null,
        };
      }),
    };
    result = mergeFormTranslateDraftContentIntoQuestions(
      result,
      locale,
      patchedPayload,
    );
  }

  return {
    questions: result,
    warnings,
    titleTranslations,
    descriptionTranslations,
  };
}
